/**
 * FLIP Expand — reusable pattern for lifting an element out of DOM flow,
 * animating it to fill a destination rect, and cleanly reversing.
 *
 * Supports two modes:
 *  1. Target FLIP: lift a [data-expand-target] element to fill an expand rect
 *  2. Column-only: slide a column element to fill its container (no target lift)
 *
 * Data-attribute protocol (target FLIP mode):
 *  [data-expand-target]      — the element to lift and animate
 *  [data-expand-collapse]    — elements that collapse/expand during the transition
 *  [data-expand-placeholder] — placeholder holding layout (created internally)
 *
 * Lifecycle:
 *  1. startExpand()    — FLIP forward: lift target, animate to expandRect
 *  2. reassertExpand() — call on every render while expanded (useLayoutEffect)
 *  3. startCollapse()  — FLIP reverse: measure settled layout, animate back
 *
 * All three operate on an opaque `expandState` object — store it in a ref.
 */

const DEFAULTS = {
  duration: 450,
  easing: "cubic-bezier(0.4, 0, 0.2, 1)",
  backdropBg: "var(--bg)",
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function setFixed(el, rect) {
  el.style.position = "fixed";
  el.style.top = `${rect.top}px`;
  el.style.left = `${rect.left}px`;
  el.style.width = `${rect.width}px`;
  el.style.height = `${rect.height}px`;
  el.style.zIndex = "9999";
  el.style.margin = "0";
  el.style.transition = "none";
}

function transitionRect(el, rect, duration, easing) {
  el.style.transition = [
    `top ${duration}ms ${easing}`,
    `left ${duration}ms ${easing}`,
    `width ${duration}ms ${easing}`,
    `height ${duration}ms ${easing}`,
  ].join(", ");
  el.style.top = `${rect.top}px`;
  el.style.left = `${rect.left}px`;
  el.style.width = `${rect.width}px`;
  el.style.height = `${rect.height}px`;
}

function createPlaceholder(rect) {
  const el = document.createElement("div");
  el.style.width = `${rect.width}px`;
  el.style.height = `${rect.height}px`;
  el.setAttribute("data-expand-placeholder", "true");
  return el;
}

function onTransitionEnd(el, prop, duration, fn) {
  let done = false;
  const finish = () => { if (done) return; done = true; el.removeEventListener("transitionend", handler); fn(); };
  const handler = (e) => { if (e.target === el && e.propertyName === prop) finish(); };
  el.addEventListener("transitionend", handler);
  setTimeout(finish, duration + 50);
}

function getInnerCollapsibles(target) {
  return [...target.querySelectorAll("[data-expand-collapse]")];
}

function getOuterCollapsibles(columnEl, target) {
  return [...columnEl.querySelectorAll("[data-expand-collapse]")]
    .filter((el) => !target.contains(el));
}

/**
 * Pin a column at its current position with explicit left/width
 * and raise it above siblings. Returns the container width.
 */
function pinColumn(columnEl, containerEl, zIndex = 15) {
  const colRect = columnEl.getBoundingClientRect();
  const containerRect = containerEl.getBoundingClientRect();
  columnEl.style.left = `${colRect.left - containerRect.left}px`;
  columnEl.style.width = `${colRect.width}px`;
  columnEl.style.right = "auto";
  columnEl.style.zIndex = String(zIndex);
  columnEl.style.transition = "none";
  return containerRect.width;
}

/**
 * Animate a pinned column to a target left/width.
 */
function slideColumn(columnEl, left, width, duration, easing) {
  columnEl.style.transition = `left ${duration}ms ${easing}, width ${duration}ms ${easing}`;
  columnEl.style.left = `${left}px`;
  columnEl.style.width = `${width}px`;
}

// ─── startExpand ─────────────────────────────────────────────────────────────

/**
 * FLIP forward: lift target out of flow, animate to expandRect.
 *
 * If `target` is null, operates in column-only mode: slides columnEl
 * (from opts) to fill its container.
 *
 * @param {HTMLElement|null} target — the [data-expand-target] element, or null for column-only
 * @param {HTMLElement} container   — reparent destination / positioning context
 * @param {DOMRect|Object} expandRect — { top, left, width, height } destination
 * @param {Object} [opts]
 * @param {number}  [opts.duration]       — ms, default 450
 * @param {string}  [opts.easing]         — CSS easing
 * @param {string}  [opts.backdropBg]     — backdrop background, default "var(--bg)"
 * @param {HTMLElement} [opts.scrollViewport] — scroll container to reset to top
 * @param {Function}    [opts.onComplete]    — called when expand animation settles
 * @param {DOMRect|Object} [opts.sourceRect] — pre-captured starting rect
 * @param {HTMLElement} [opts.columnEl]      — column element (column-only mode)
 * @returns {Object} expandState — pass to reassertExpand / startCollapse
 */
export function startExpand(target, container, expandRect, opts = {}) {
  const {
    duration = DEFAULTS.duration,
    easing = DEFAULTS.easing,
    backdropBg = DEFAULTS.backdropBg,
    scrollViewport,
    onComplete,
    sourceRect,
    columnEl,
  } = opts;

  // ── Column-only mode (no target FLIP) ────────────────────────
  if (!target && columnEl) {
    const containerW = pinColumn(columnEl, container);
    void columnEl.offsetHeight;
    slideColumn(columnEl, 0, containerW, duration, easing);

    const state = {
      targetEl: null, placeholderEl: null, backdropEl: null,
      columnEl, collapsing: false, cancelled: false,
    };
    onTransitionEnd(columnEl, "width", duration, () => {
      if (!state.cancelled && onComplete) onComplete(state);
    });
    return state;
  }

  // ── Full target FLIP mode ────────────────────────────────────
  const targetRect = sourceRect || target.getBoundingClientRect();

  // Placeholder to hold scroll layout
  const placeholder = createPlaceholder(targetRect);
  target.parentNode.insertBefore(placeholder, target);

  // Backdrop covering destination area
  const backdrop = document.createElement("div");
  backdrop.style.cssText = [
    "position:fixed",
    `top:${expandRect.top}px`,
    `left:${expandRect.left}px`,
    `width:${expandRect.width}px`,
    `height:${expandRect.height}px`,
    "z-index:9998",
    `background:${backdropBg}`,
    "opacity:0",
    "transition:none",
    "pointer-events:none",
  ].join(";");
  container.appendChild(backdrop);

  // Reparent to container (escapes stacking contexts, stays in React root)
  container.appendChild(target);

  // Position at current rect
  setFixed(target, targetRect);

  // Lock inner collapsibles at current height
  const inner = getInnerCollapsibles(target);
  inner.forEach((el) => {
    el.style.height = `${el.offsetHeight}px`;
    el.style.overflow = "hidden";
    el.style.transition = "none";
  });

  if (scrollViewport) scrollViewport.scrollTop = 0;

  void target.offsetHeight;

  // Animate to destination
  transitionRect(target, expandRect, duration, easing);

  // Fade in backdrop
  backdrop.style.transition = `opacity ${duration * 0.6}ms ease-out`;
  backdrop.style.opacity = "1";

  // Collapse inner collapsibles
  inner.forEach((el) => {
    el.style.transition = `height ${duration * 0.8}ms ${easing}, opacity ${duration * 0.4}ms ease-out`;
    el.style.height = "0px";
    el.style.opacity = "0";
  });

  const state = {
    targetEl: target,
    placeholderEl: placeholder,
    backdropEl: backdrop,
    collapsing: false,
    cancelled: false,
  };

  onTransitionEnd(target, "width", duration, () => {
    // Guarded like the column-only branch: cancelExpand mid-animation must
    // not let the pending fallback timer fire a stale onComplete — the
    // ContentShell completion re-establishes column overrides, silently
    // re-locking a column whose takeover was already torn down.
    if (!state.cancelled && onComplete) onComplete(state);
  });

  return state;
}

// ─── cancelExpand ────────────────────────────────────────────────────────────

/**
 * Instant teardown of a lifted target — no animation, no measurement.
 * Two callers, two shapes of mess:
 *  - The owning VIEW is being switched away: React removed the column's
 *    body (taking the placeholder with it) but not the reparented target,
 *    which survives as a fixed-position ghost with dead handlers over the
 *    new view. Remove the ghost outright.
 *  - A COMPETING column animation (SplitPage's detail-panel FLIP) is about
 *    to write inline styles on the same column: the placeholder is still
 *    in flow, so put the target back where React expects it and clear the
 *    lift's styles before the other system runs.
 * Column styles/overrides are the caller's to restore — this only undoes
 * what startExpand did to the target, placeholder, backdrop, and
 * collapsibles.
 */
export function cancelExpand(expandState, columnEl) {
  const { targetEl, placeholderEl, backdropEl } = expandState;
  expandState.cancelled = true;
  expandState.collapsing = true;
  if (targetEl) {
    if (placeholderEl?.isConnected) {
      placeholderEl.parentNode.insertBefore(targetEl, placeholderEl);
      targetEl.style.cssText = "";
      getInnerCollapsibles(targetEl).forEach((el) => { el.style.cssText = ""; });
    } else {
      targetEl.remove();
    }
  }
  placeholderEl?.remove();
  backdropEl?.remove();
  if (columnEl && targetEl) {
    getOuterCollapsibles(columnEl, targetEl).forEach((el) => {
      el.style.position = "";
      el.style.visibility = "";
      el.style.height = "";
      el.style.overflow = "";
      el.style.opacity = "";
      el.style.transition = "";
    });
  }
}

// ─── startCollapse ───────────────────────────────────────────────────────────

/**
 * FLIP reverse: animate back from expanded state.
 *
 * In target mode: reparent target back into flow, measure where it settles,
 * then animate from expanded rect to settled rect.
 *
 * In column-only mode (expandState.targetEl is null): shrink the column
 * back to targetWidth.
 *
 * @param {Object} expandState     — from startExpand
 * @param {HTMLElement} container   — same container used for expand
 * @param {HTMLElement} columnEl    — the column element (for finding outer collapsibles)
 * @param {Object} [opts]
 * @param {number}   [opts.duration]         — ms, default 450
 * @param {string}   [opts.easing]           — CSS easing
 * @param {number}   [opts.targetWidth]      — final column width (column-only mode)
 * @param {Function} [opts.onBeforeMeasure]  — called after styles cleared, before measuring
 * @param {Function} [opts.onBeforeAnimate]  — called after FLIP setup, before animation starts
 * @param {Function} [opts.onComplete]       — called when collapse animation settles
 */
export function startCollapse(expandState, container, columnEl, opts = {}) {
  const {
    duration = DEFAULTS.duration,
    easing = DEFAULTS.easing,
    targetWidth,
    onBeforeMeasure,
    onBeforeAnimate,
    onComplete,
  } = opts;

  // ── Column-only mode ─────────────────────────────────────────
  if (!expandState.targetEl) {
    const col = expandState.columnEl || columnEl;

    // Pin at full width
    const containerW = container.getBoundingClientRect().width;
    col.style.left = "0px";
    col.style.width = `${containerW}px`;
    col.style.right = "auto";
    col.style.zIndex = "15";
    col.style.transition = "none";

    void col.offsetHeight;

    // Animate to target width
    slideColumn(col, 0, targetWidth, duration, easing);

    onTransitionEnd(col, "width", duration, () => {
      col.style.removeProperty("width");
      col.style.right = "";
      col.style.zIndex = "";
      col.style.transition = "";
      if (onComplete) onComplete();
    });
    return;
  }

  // ── Full target FLIP reverse ─────────────────────────────────
  const { targetEl, placeholderEl, backdropEl } = expandState;
  const inner = getInnerCollapsibles(targetEl);

  // ── FIRST: record current expanded rect ──
  const expandedRect = targetEl.getBoundingClientRect();

  // ── LAST: reparent back, clear styles, measure settled position ──
  // (All synchronous — no paint until after animation starts)
  placeholderEl.parentNode.insertBefore(targetEl, placeholderEl);
  placeholderEl.remove();

  targetEl.style.cssText = "";
  // Clear inner styles but keep invisible to prevent flash during measurement
  inner.forEach((el) => {
    el.style.cssText = "";
    el.style.opacity = "0";
    el.style.transition = "none";
  });

  const outer = getOuterCollapsibles(columnEl, targetEl);
  outer.forEach((el) => {
    el.style.position = "";
    el.style.visibility = "";
    el.style.height = "";
    el.style.overflow = "";
    el.style.opacity = "";
    el.style.transition = "";
  });

  // Hook: adjust layout before measuring (e.g. restore column to narrow width)
  if (onBeforeMeasure) onBeforeMeasure();

  void targetEl.offsetHeight;
  const destinationRect = targetEl.getBoundingClientRect();

  // Measure inner natural heights while they're in flow
  const innerHeights = inner.map((el) => el.scrollHeight);

  // ── INVERT: placeholder at destination, target at expanded rect ──
  const newPlaceholder = createPlaceholder(destinationRect);
  targetEl.parentNode.insertBefore(newPlaceholder, targetEl);

  container.appendChild(targetEl);
  setFixed(targetEl, expandedRect);

  // Inner collapsibles start collapsed (matching expanded visual state)
  inner.forEach((el) => {
    el.style.height = "0px";
    el.style.overflow = "hidden";
    el.style.opacity = "0";
    el.style.transition = "none";
  });

  outer.forEach((el) => {
    el.style.height = "0px";
    el.style.overflow = "hidden";
    el.style.opacity = "0";
    el.style.transition = "none";
  });

  expandState.placeholderEl = newPlaceholder;
  expandState.collapsing = true;

  // Hook: start coordinated animations (e.g. column shrink from full→narrow)
  if (onBeforeAnimate) onBeforeAnimate({ destinationRect });

  void targetEl.offsetHeight;

  // ── PLAY: animate to destination ──
  transitionRect(targetEl, destinationRect, duration, easing);

  backdropEl.style.transition = `opacity ${duration * 0.7}ms ease-out`;
  backdropEl.style.opacity = "0";

  outer.forEach((el) => {
    const h = el.scrollHeight;
    el.style.transition = `height ${duration}ms ${easing}, opacity ${duration * 0.5}ms ease-in ${duration * 0.3}ms`;
    el.style.height = `${h}px`;
    el.style.opacity = "1";
  });

  // Inner collapsibles animate from collapsed → visible (reverse of expand)
  inner.forEach((el, i) => {
    el.style.transition = `height ${duration * 0.8}ms ${easing}, opacity ${duration * 0.4}ms ease-in ${duration * 0.4}ms`;
    el.style.height = `${innerHeights[i]}px`;
    el.style.opacity = "1";
  });

  onTransitionEnd(targetEl, "width", duration, () => {
    // Final cleanup — restore everything to natural state
    targetEl.style.cssText = "";
    inner.forEach((el) => { el.style.cssText = ""; });
    outer.forEach((el) => { el.style.cssText = ""; });

    newPlaceholder.parentNode.insertBefore(targetEl, newPlaceholder);
    newPlaceholder.remove();
    backdropEl.remove();

    if (onComplete) onComplete();
  });
}

// ─── reassertExpand ──────────────────────────────────────────────────────────

/**
 * Re-assert expanded position after React re-renders.
 * Call from useLayoutEffect on every render while expanded.
 * No-op if state is null or currently collapsing.
 *
 * @param {Object|null} expandState — from startExpand (or null)
 * @param {DOMRect|Object} expandRect — current expand destination rect
 */
export function reassertExpand(expandState, expandRect) {
  if (!expandState || expandState.collapsing) return;
  if (expandState.targetEl) setFixed(expandState.targetEl, expandRect);
}

